import React, { useEffect, useMemo, useState } from 'react';
import { FaPen, FaTrash } from 'react-icons/fa';

// Simple Kanban board for CRM Leads
// - Columns are persisted in localStorage under 'crm_leads_columns'
// - Lead column positions are persisted under 'crm_leads_positions'
// - Data fetched from GET `${import.meta.env.VITE_URL_API}/leads` with Authorization Bearer token

// Agora as colunas vêm do backend via GET /leads/etapas (por usuário)
// e os cards de cada coluna via GET /leads/etapas/{funil}

const Card = ({ lead, onOpen, etapa, etapaTitle, etapaColor }) => {
  const meta = lead?.metadataLEAD || {};
  const hexToRgba = (hex, alpha=0.15) => {
    if (!hex) return `rgba(0,0,0,${alpha})`;
    const h = hex.replace('#','');
    const bigint = parseInt(h.length===3 ? h.split('').map(x=>x+x).join('') : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  const tint = hexToRgba(etapaColor, 0.12);
  const border = hexToRgba(etapaColor, 0.35);
  return (
    <div
      onClick={()=> onOpen && onOpen(lead)}
      style={{
        background: tint,
        border: `1px solid ${border}`,
        borderRadius: 8,
        padding: 10,
        marginBottom: 10,
        boxShadow: '0 1px 2px rgba(0,0,0,.04)',
        cursor: 'pointer',
      }}
      title={`Lead #${lead.codigoLEAD}`}
    >
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
        <span style={{ fontSize:11, background: etapaColor || '#e5e7eb', color:'#111827', padding:'2px 6px', borderRadius:999 }}>{etapaTitle || etapa}</span>
        {lead.statusLEAD && (
          <span style={{ fontSize:11, background:'#111827', color:'#fff', padding:'2px 6px', borderRadius:999 }}>{lead.statusLEAD}</span>
        )}
      </div>
      <div
        draggable
        onDragStart={(e)=>{
          e.dataTransfer.setData('application/lead-id', String(lead.codigoLEAD));
          if (etapa) e.dataTransfer.setData('application/lead-from', String(etapa));
        }}
        title="Arraste para mover"
        style={{ fontSize: 11, color: '#6b7280', userSelect: 'none' }}
      >
        ⋮⋮ mover
      </div>
      <div style={{ fontWeight: 700 }}>{lead.nomeLEAD}</div>
      <div style={{ color: '#374151', fontSize: 13 }}>{lead.emailLEAD || '—'}</div>
      <div style={{ color: '#374151', fontSize: 13 }}>{lead.telefoneLEAD || '—'}</div>
      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 6 }}>Origem: {lead.origemLEAD || '—'} · Canal: {meta?.canal || '—'}</div>
      <div style={{ color: '#6b7280', fontSize: 12 }}>Imóvel: {lead.imovel?.tituloIMOVEL || lead.imovelLEAD || '—'}</div>
      <div style={{ color: '#6b7280', fontSize: 12 }}>Cliente: {lead.usuario?.nomeUSUARIO || lead.usuarioLEAD || '—'}</div>
      <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 4 }}>
        {new Date(lead.dataCRIACAO).toLocaleString()}
      </div>
    </div>
  );
};

const Column = ({ col, leads, onDropLead, onOpenLead }) => (
  <div
    onDragOver={(e)=>{ e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
    onDrop={(e)=>{
      const id = e.dataTransfer.getData('application/lead-id');
      if (id) onDropLead(Number(id), col.id);
    }}
    style={{
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      padding: 12,
      minWidth: 260,
      width: 300,
      maxWidth: 340,
      maxHeight: 'calc(100vh - 240px)',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      borderTop: `4px solid ${col.color || '#e5e7eb'}`
    }}
  >
    <div style={{ fontWeight: 800, marginBottom: 8, color: '#111827' }}>{col.title}</div>
    {leads.map(l => <Card key={l.codigoLEAD} lead={l} onOpen={onOpenLead} etapa={col.id} etapaTitle={col.id==='0' ? 'Início' : col.title} etapaColor={col.color} />)}
  </div>
);

export default function CRMLeads() {
  const [columns, setColumns] = useState([]); // [{id:'0', title:'Início', color:'#hex', fixed:true, ordem:0}, ...]
  const [leadsByEtapa, setLeadsByEtapa] = useState({}); // mapa chave -> leads
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [historicos, setHistoricos] = useState([]);
  const [histTipo, setHistTipo] = useState('nota');
  const [histCanal, setHistCanal] = useState('');
  const [histAnot, setHistAnot] = useState('');
  const [updating, setUpdating] = useState(false);
  const [serverEtapas, setServerEtapas] = useState([]); // chaves vindas do backend

  // Helpers API
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const api = (path, opts={}) => fetch(`${import.meta.env.VITE_URL_API}${path}`, {
    ...opts,
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {})
    }
  });

  // Load columns and leads
  const refreshBoard = async () => {
    const resCfg = await api('/leads/etapas-config');
    if (!resCfg.ok) throw new Error('Falha ao carregar etapas-config');
    const cfg = await resCfg.json(); // [{ chave, nome, cor, ordem, fixa }]
    // Garante "0" no início
    const sorted = (Array.isArray(cfg) ? cfg : []).sort((a,b)=> (a.ordem??0)-(b.ordem??0));
    const cols = sorted.map(c => ({ id: String(c.chave), title: String(c.chave) === '0' ? 'Início' : (c.nome || c.chave), color: c.cor || '', fixed: !!c.fixa, ordem: c.ordem ?? 0 }));
    setColumns(cols);
    setServerEtapas(cols.map(c=>c.id));
    const entries = await Promise.all(cols.map(async c => {
      const r = await api(`/leads/etapas/${encodeURIComponent(c.id)}`);
      const arr = r.ok ? await r.json() : [];
      return [c.id, Array.isArray(arr)?arr:[]];
    }));
    setLeadsByEtapa(Object.fromEntries(entries));
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true); setError('');
    (async () => {
      try {
        await refreshBoard();
      } catch (err) {
        console.error('CRMLeads etapas-config error', err);
        if (mounted) setError('Falha ao carregar etapas/leads');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, []);

  // DnD -> PATCH funil e move no estado local
  const moveLeadTo = async (leadId, newEtapa) => {
    try {
      const isServerCol = serverEtapas.includes(newEtapa);
      if (isServerCol) {
        const etapaServer = newEtapa;
        await api(`/leads/${leadId}/funil`, { method: 'PATCH', body: JSON.stringify({ funil: etapaServer }) });
        // Move localmente
        setLeadsByEtapa(prev => {
          const next = { ...prev };
          for (const k of Object.keys(next)) next[k] = (next[k] || []).filter(l => l.codigoLEAD !== leadId);
          const moved = Object.values(prev).flat().find(l => l.codigoLEAD === leadId);
          if (moved) {
            const col = next[newEtapa] || [];
            next[newEtapa] = [ { ...moved, funilLEAD: etapaServer }, ...col];
          }
          return next;
        });
      }
    } catch (e) {
      console.error('Falha ao mover lead', e);
    }
  };

  // API helpers já definidos acima

  const openLead = async (lead) => {
    setSelected(lead);
    setHistoricos([]);
    setHistLoading(true);
    try {
      const res = await api(`/leads/${lead.codigoLEAD}/historicos`);
      const data = res.ok ? await res.json() : [];
      setHistoricos(Array.isArray(data) ? data : []);
    } catch { setHistoricos([]); }
    finally { setHistLoading(false); }
  };

  const createHistorico = async () => {
    if (!selected) return;
    setUpdating(true);
    try {
      // Envia com nomes esperados pelo backend e aliases para compatibilidade
      const payload = {
        tipo: histTipo || undefined,
        canal: histCanal || undefined,
        anotacao: histAnot || undefined,
        // aliases backend
        tipoHISTLEAD: histTipo || undefined,
        canalHISTLEAD: histCanal || undefined,
        anotacaoHISTLEAD: histAnot || undefined,
      };
      await api(`/leads/${selected.codigoLEAD}/historicos`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      // refresh list
      const res = await api(`/leads/${selected.codigoLEAD}/historicos`);
      const data = res.ok ? await res.json() : [];
      setHistoricos(Array.isArray(data) ? data : []);
      setHistAnot('');
    } catch {}
    finally { setUpdating(false); }
  };

  const patchFunil = async (funil) => {
    if (!selected) return;
    setUpdating(true);
    try {
      await api(`/leads/${selected.codigoLEAD}/funil`, { method: 'PATCH', body: JSON.stringify({ funil }) });
      // update selected local copy
      setSelected(prev => prev ? { ...prev, funilLEAD: funil } : prev);
    } catch {}
    finally { setUpdating(false); }
  };

  const patchStatus = async (status) => {
    if (!selected) return;
    setUpdating(true);
    try {
      await api(`/leads/${selected.codigoLEAD}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setSelected(prev => prev ? { ...prev, statusLEAD: status } : prev);
    } catch {}
    finally { setUpdating(false); }
  };

  // Colunas CRUD via API
  const addColumn = async () => {
    const nome = prompt('Nome da nova coluna:');
    if (!nome) return;
    const cor = prompt('Cor (hex, opcional):', '#E5E7EB') || undefined;
    // chave pode ser derivada do nome; backend pode slugificar
    const body = { nome, cor };
    const res = await api('/leads/etapas-config', { method: 'POST', body: JSON.stringify(body) });
    if (!res.ok) { alert('Falha ao criar coluna'); return; }
    await refreshBoard();
  };

  const editColumn = async (chave) => {
    if (String(chave) === '0') return;
    const current = columns.find(c=>c.id===chave);
    const nome = prompt('Novo nome da coluna:', current?.title || '') || undefined;
    const cor = prompt('Cor (hex):', current?.color || '#E5E7EB') || undefined;
    const res = await api(`/leads/etapas-config/${encodeURIComponent(chave)}`, { method: 'PATCH', body: JSON.stringify({ nome, cor }) });
    if (!res.ok) { alert('Falha ao editar coluna'); return; }
    await refreshBoard();
  };

  const removeColumn = async (chave) => {
    if (String(chave) === '0') { alert('A coluna Início não pode ser removida.'); return; }
    if (!confirm('Remover esta coluna? Se houver leads, será necessário migrar.')) return;
    let res = await api(`/leads/etapas-config/${encodeURIComponent(chave)}`, { method: 'DELETE' });
    if (res.status === 409) {
      const destino = prompt('A etapa possui leads. Informe a chave de destino para migrar (ex.: negociacao):');
      if (!destino) return;
      res = await api(`/leads/etapas-config/${encodeURIComponent(chave)}?migrarPara=${encodeURIComponent(destino)}`, { method: 'DELETE' });
    }
    if (!res.ok) { alert('Falha ao remover coluna'); return; }
    await refreshBoard();
  };

  const moveColumn = async (chave, dir) => {
    // Reordena localmente e envia PATCH /reorder (sem a "0", que sempre fica em primeiro)
    const rest = columns.filter(c=>c.id!=='0').map(c=>c.id);
    const idx = rest.indexOf(chave);
    if (idx<0) return;
    const j = dir === 'left' ? idx-1 : idx+1;
    if (j<0 || j>=rest.length) return;
    [rest[idx], rest[j]] = [rest[j], rest[idx]];
    const res = await api('/leads/etapas-config/reorder', { method: 'PATCH', body: JSON.stringify({ ordem: rest }) });
    if (!res.ok) { alert('Falha ao reordenar'); return; }
    await refreshBoard();
  };

  return (
    <div style={{ padding: 16, width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'auto', boxSizing: 'border-box', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>CRM Leads</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={addColumn} style={{ padding: '8px 12px', background: '#34574A', color: '#fff', border: 0, borderRadius: 8, fontWeight: 700 }}>Adicionar Etapa</button>
        </div>
      </div>
      {error && <div style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', padding: 8, borderRadius: 8, marginBottom: 8 }}>{error}</div>}
      {loading ? (
        <div>Carregando leads...</div>
      ) : (
        <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'hidden', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', gap: 12, overflowX: 'auto', overflowY: 'hidden', paddingBottom: 8, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overscrollBehaviorX: 'contain', WebkitOverflowScrolling: 'touch' }}>
            {columns.map(col => (
              <div key={col.id} style={{ display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
                  <div style={{ fontWeight:800 }}>{col.id==='0' ? 'Início' : col.title}</div>
                  {col.id !== '0' && (
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={()=>moveColumn(col.id,'left')} title="Mover coluna à esquerda" style={{ background:'transparent', border: '1px solid #e5e7eb', borderRadius:6, padding:'4px 8px', cursor:'pointer' }}>←</button>
                      <button onClick={()=>moveColumn(col.id,'right')} title="Mover coluna à direita" style={{ background:'transparent', border: '1px solid #e5e7eb', borderRadius:6, padding:'4px 8px', cursor:'pointer' }}>→</button>
                      <button onClick={()=>editColumn(col.id)} title="Editar coluna" style={{ background:'#34574A', color:'#fff', border:0, borderRadius:6, padding:'6px 8px', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                        <FaPen />
                      </button>
                      <button onClick={()=>removeColumn(col.id)} title="Remover coluna" style={{ background:'transparent', color:'#b91c1c', border: '1px solid #fecaca', borderRadius:6, padding:'6px 8px', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </div>
                <Column col={{...col}} leads={(leadsByEtapa[col.id] || [])} onDropLead={moveLeadTo} onOpenLead={openLead} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de detalhes do Lead */}
      {selected && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div style={{ width: 'min(100%, 960px)', background: '#fff', borderRadius: 12, boxShadow: '0 20px 50px rgba(0,0,0,.3)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #eee' }}>
              <strong>Lead #{selected.codigoLEAD} · {selected.nomeLEAD}</strong>
              <button onClick={()=>setSelected(null)} style={{ background: 'transparent', border: 0, fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 12, padding: 16, maxHeight: '75vh', overflow: 'auto' }}>
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:8, padding:12 }}>
                  <div style={{ fontWeight:700, marginBottom: 8 }}>Dados do Lead</div>
                  <div><b>Nome:</b> {selected.nomeLEAD}</div>
                  <div><b>Email:</b> {selected.emailLEAD || '—'}</div>
                  <div><b>Telefone:</b> {selected.telefoneLEAD || '—'}</div>
                  <div><b>Origem:</b> {selected.origemLEAD || '—'} · <b>Canal:</b> {selected.metadataLEAD?.canal || '—'}</div>
                  <div><b>Funil:</b> {selected.funilLEAD || '—'} · <b>Status:</b> {selected.statusLEAD || '—'}</div>
                  <div><b>Data:</b> {new Date(selected.dataCRIACAO).toLocaleString()}</div>
                </div>
                <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:8, padding:12 }}>
                  <div style={{ fontWeight:700, marginBottom: 8 }}>Imóvel</div>
                  <div><b>Código:</b> {selected.imovel?.codigoIMOVEL || selected.imovelLEAD}</div>
                  <div><b>Título:</b> {selected.imovel?.tituloIMOVEL || '—'}</div>
                  <div><b>Cidade:</b> {selected.imovel?.cityIMOVEL || '—'} · <b>UF:</b> {selected.imovel?.stateIMOVEL || '—'}</div>
                  <div><b>Valor:</b> {selected.imovel?.valorIMOVEL ? `${selected.imovel.valorIMOVEL} ${selected.imovel.labelvalorIMOVEL||''}` : '—'}</div>
                </div>
                <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:8, padding:12 }}>
                  <div style={{ fontWeight:700, marginBottom: 8 }}>Usuário</div>
                  <div><b>Código:</b> {selected.usuario?.codigoUSUARIO || selected.usuarioLEAD || '—'}</div>
                  <div><b>Nome:</b> {selected.usuario?.nomeUSUARIO || '—'}</div>
                  <div><b>Email:</b> {selected.usuario?.emailUSUARIO || '—'}</div>
                  <div><b>Whatsapp:</b> {selected.usuario?.whatsappUSUARIO || '—'}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:8, padding:12 }}>
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
                    <div style={{ fontWeight:700, flex:1 }}>Histórico</div>
                    {histLoading && <span style={{ color:'#6b7280' }}>Carregando...</span>}
                  </div>
                  <div style={{ display:'grid', gap:8, maxHeight: 260, overflow:'auto', paddingRight: 4 }}>
                    {historicos.length === 0 && !histLoading && (
                      <div style={{ color:'#6b7280' }}>Sem históricos.</div>
                    )}
                    {[...historicos]
                      .sort((a,b)=>{
                        const da = new Date(a.dataHISTORICO || a.data || a.createdAt || a.dataCRIACAO || 0).getTime();
                        const db = new Date(b.dataHISTORICO || b.data || b.createdAt || b.dataCRIACAO || 0).getTime();
                        return db - da;
                      })
                      .map((h, idx)=> {
                        const whenRaw = h.dataHISTORICO || h.data || h.createdAt || h.dataCRIACAO;
                        let whenStr = '—';
                        try { if (whenRaw) whenStr = new Date(whenRaw).toLocaleString(); } catch {}
                        const tipo = h.tipo || h.tipoHISTLEAD || '—';
                        const canal = h.canal || h.canalHISTLEAD;
                        const anot = h.anotacao || h.anotacaoHISTLEAD;
                        const de = h.deHISTLEAD;
                        const para = h.paraHISTLEAD;
                        return (
                          <div key={idx} style={{ border:'1px solid #eee', borderRadius:6, padding:8 }}>
                            <div style={{ fontSize:12, color:'#6b7280' }}>{whenStr}</div>
                            <div><b>Tipo:</b> {tipo}</div>
                            {tipo === 'funil' && (de || para) && (
                              <div style={{ fontSize:12, color:'#374151' }}>De: {de ?? '—'} → Para: {para ?? '—'}</div>
                            )}
                            {canal && <div><b>Canal:</b> {canal}</div>}
                            {anot && <div style={{ whiteSpace:'pre-wrap' }}>{anot}</div>}
                          </div>
                        );
                      })}
                  </div>
                  <div style={{ borderTop:'1px solid #eee', marginTop:8, paddingTop:8, display:'grid', gap:8 }}>
                    <div style={{ display:'flex', gap:8 }}>
                      <select value={histTipo} onChange={e=>setHistTipo(e.target.value)} style={{ flex:1, padding:8, border:'1px solid #e5e7eb', borderRadius:6 }}>
                        <option value="nota">Nota</option>
                        <option value="contato">Contato</option>
                        <option value="outro">Outro</option>
                      </select>
                      <select value={histCanal} onChange={e=>setHistCanal(e.target.value)} style={{ flex:1, padding:8, border:'1px solid #e5e7eb', borderRadius:6 }}>
                        <option value="">Canal (opcional)</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="email">Email</option>
                        <option value="ligacao">Ligação</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                    <textarea value={histAnot} onChange={e=>setHistAnot(e.target.value)} placeholder="Anotação (opcional)" rows={3} style={{ padding:8, border:'1px solid #e5e7eb', borderRadius:6 }} />
                    <button disabled={updating} onClick={createHistorico} style={{ padding:'10px 12px', background:'#34574A', color:'#fff', border:0, borderRadius:8, fontWeight:700 }}>
                      {updating ? 'Salvando...' : 'Adicionar histórico'}
                    </button>
                  </div>
                </div>
                <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:8, padding:12, display:'grid', gap:8 }}>
                  <div style={{ fontWeight:700 }}>Atualizações</div>
                  <div style={{ display:'flex', gap:8 }}>
                    <select value={selected.funilLEAD ?? ''} onChange={e=>patchFunil(e.target.value)} style={{ flex:1, padding:8, border:'1px solid #e5e7eb', borderRadius:6 }}>
                      <option value="">Funil (selecione)</option>
                      {columns.map(c => (
                        <option key={c.id} value={c.id}>{c.id==='0' ? 'Início' : c.title}</option>
                      ))}
                    </select>
                    <select value={selected.statusLEAD || ''} onChange={e=>patchStatus(e.target.value)} style={{ flex:1, padding:8, border:'1px solid #e5e7eb', borderRadius:6 }}>
                      <option value="">Status (selecione)</option>
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                      <option value="convertido">Convertido</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
